import { defineStore } from "pinia";
import { TextImage } from "@/types/entity1";
import api from "@/modules/api";
import { getDisplayDateTime } from "@/modules/common";

const PER_PAGE = 6;

interface TextImageState {
  unmarkedCount: number;
  unmarkedIdList: {
    data: number[];
    init: boolean;
  };
  queuedIdList: {
    data: number[];
    requestTimeMs: number | null;
    intervalId: number | null;
    init: boolean;
  };
  likedIdList: {
    data: number[];
    init: boolean;
  };
  dislikeIdList: {
    data: number[];
    init: boolean;
  };
  map: { [id: number]: TextImage };
}

export const useTextImage = defineStore("textImage", {
  state: () => {
    return {
      unmarkedCount: 0,
      unmarkedIdList: {
        data: [],
        changed: false,
        init: false,
      },
      queuedIdList: {
        data: [],
        changed: false,
        requestTimeMs: null,
        intervalId: null,
        init: false,
      },
      likedIdList: {
        data: [],
        changed: false,
        init: false,
      },
      dislikeIdList: {
        data: [],
        changed: false,
        init: false,
      },
      map: {},
    } as TextImageState;
  },
  getters: {
    unmarkedList: (state: TextImageState) => {
      return state.unmarkedIdList.data.map((id) => state.map[id]);
    },
    likedList: (state: TextImageState) => {
      return state.likedIdList.data.map((id) => state.map[id]);
    },
    dislikeList: (state: TextImageState) => {
      return state.dislikeIdList.data.map((id) => state.map[id]);
    },
    queuedList: (state: TextImageState) => {
      return state.queuedIdList.data.map((id) => state.map[id]);
    },
    toMarkList: (state: TextImageState) => {
      return state.unmarkedIdList.data.map((id) => state.map[id]);
    },
  },
  actions: {
    useCursorEncode(dtUpdate: string, pointsToNextItems = false): string {
      return btoa(
        JSON.stringify({
          dtUpdate: getDisplayDateTime(dtUpdate, true, true),
          _pointsToNextItems: pointsToNextItems,
        })
      );
    },
    async initUnmarkedListIfAbsent(force = false) {
      const state = this.unmarkedIdList;
      if (force || !state.init) {
        const list = await this.fetchUnmarkedTextImage();
        state.data = list.map((item) => item["id"]);
        state.init = true;
      }
    },
    async getNewerUnmarkedList() {
      const currentList = this.unmarkedIdList.data;
      if (currentList.length === 0) {
        await this.initUnmarkedListIfAbsent(true);
      } else {
        const firstItem = this.map[currentList[0]];
        const cursor = this.useCursorEncode(firstItem.dtUpdate, false);
        const list = await this.fetchUnmarkedTextImage(cursor);
        currentList.unshift(...list.map((item) => item["id"]));
        if (list.length > 0) {
          this.fetchUnmarkedCount();
        }
      }
    },
    async getOlderUnmarkedList() {
      const currentList = this.unmarkedIdList.data;
      if (currentList.length === 0) {
        await this.initUnmarkedListIfAbsent(true);
        return this.unmarkedIdList.data.length !== 0;
      } else {
        const lastItem = this.map[currentList[currentList.length - 1]];
        const cursor = this.useCursorEncode(lastItem.dtUpdate, true);
        const list = await this.fetchUnmarkedTextImage(cursor);
        currentList.push(...list.map((item) => item["id"]));
        return list.length !== 0;
      }
    },
    async initQueuedListIfAbsent(force = false) {
      const state = this.queuedIdList;
      if (force || !state.init) {
        const list = await this.fetchQueuedList();
        state.data = list.map((item) => item["id"]);
        state.init = true;
        this.resetAndStartInterval();
      }
    },
    async getNewerQueuedList() {
      const currentList = this.queuedIdList.data;
      if (currentList.length === 0) {
        await this.initQueuedListIfAbsent(true);
      } else {
        const firstItem = this.map[currentList[0]];
        const cursor = this.useCursorEncode(firstItem.dtUpdate, false);
        const list = await this.fetchQueuedList(cursor);
        currentList.unshift(...list.map((item) => item["id"]));
      }
    },
    async getOlderQueuedLList() {
      const currentList = this.queuedIdList.data;
      if (currentList.length === 0) {
        await this.initQueuedListIfAbsent(true);
        return this.queuedIdList.data.length !== 0;
      } else {
        const lastItem = this.map[currentList[currentList.length - 1]];
        const cursor = this.useCursorEncode(lastItem.dtUpdate, true);
        const list = await this.fetchQueuedList(cursor);
        currentList.push(...list.map((item) => item["id"]));
        return list.length !== 0;
      }
    },
    async initLikeListIfAbsent(force = false) {
      const state = this.likedIdList;
      if (force || !state.init) {
        const list = await this.fetchLikedTextImage();
        state.data = list.map((item) => item["id"]);
        state.init = true;
      }
    },
    async getNewerLikeList() {
      const currentList = this.likedIdList.data;
      if (currentList.length === 0) {
        await this.initLikeListIfAbsent(true);
      } else {
        const firstItem = this.map[currentList[0]];
        const cursor = this.useCursorEncode(firstItem.dtUpdate, false);
        const list = await this.fetchLikedTextImage(cursor);
        currentList.unshift(...list.map((item) => item["id"]));
      }
    },
    async getOlderLikeList() {
      const currentList = this.likedIdList.data;
      if (currentList.length === 0) {
        await this.initLikeListIfAbsent(true);
        return this.likedIdList.data.length !== 0;
      } else {
        const lastItem = this.map[currentList[currentList.length - 1]];
        const cursor = this.useCursorEncode(lastItem.dtUpdate, true);
        const list = await this.fetchLikedTextImage(cursor);
        currentList.push(...list.map((item) => item["id"]));
        return list.length !== 0;
      }
    },
    async initDislikeListIfAbsent(force = false) {
      const state = this.dislikeIdList;
      if (force || !state.init) {
        const list = await this.fetchDislikedTextImage();
        state.data = list.map((item) => item["id"]);
        state.init = true;
      }
    },
    async getNewerDislikeList() {
      const currentList = this.dislikeIdList.data;
      if (currentList.length === 0) {
        await this.initDislikeListIfAbsent(true);
      } else {
        const firstItem = this.map[currentList[0]];
        const cursor = this.useCursorEncode(firstItem.dtUpdate, false);
        const list = await this.fetchDislikedTextImage(cursor);
        currentList.unshift(...list.map((item) => item["id"]));
      }
    },
    async getOlderDislikeList() {
      const currentList = this.dislikeIdList.data;
      if (currentList.length === 0) {
        await this.initDislikeListIfAbsent(true);
        return this.dislikeIdList.data.length !== 0;
      } else {
        const lastItem = this.map[currentList[currentList.length - 1]];
        const cursor = this.useCursorEncode(lastItem.dtUpdate, true);
        const list = await this.fetchDislikedTextImage(cursor);
        currentList.push(...list.map((item) => item["id"]));
        return list.length !== 0;
      }
    },
    resetAndStartInterval() {
      if (this.queuedIdList.intervalId) {
        window.clearInterval(this.queuedIdList.intervalId!);
      }
      this.queuedIdList.intervalId = null;
      if (this.queuedIdList.data.length === 0) {
        return;
      }
      const target = this.map[this.queuedIdList.data[0]];
      this.queuedIdList.requestTimeMs = Date.parse(target.dtNow);
      this.queuedIdList.intervalId = window.setInterval(() => {
        if (this.queuedIdList.data.length === 0) {
          window.clearInterval(this.queuedIdList.intervalId!);
          this.queuedIdList.intervalId = null;
          this.queuedIdList.requestTimeMs = null;
          return;
        }
        this.queuedIdList.requestTimeMs! += 1000;
      }, 1000);
    },
    async fetchQueuedList(cursor?: string, perPage = PER_PAGE) {
      const res = await api.readTextImagePage({
        pagination: { perPage },
        sort: { field: "dtUpdate", order: "desc" },
        cursor,
      });
      const textImageList = res.page.data;
      this.updateTextImageMap(textImageList);
      return textImageList;
    },
    async fetchUnmarkedTextImage(cursor?: string, perPage = PER_PAGE) {
      const res = await api.readTextImagePage({
        filter: { status: 0 },
        sort: { field: "dtUpdate", order: "desc" },
        pagination: { perPage },
        cursor,
      });
      this.updateTextImageMap(res.page.data);
      return res.page.data;
    },
    async fetchLikedTextImage(cursor?: string, perPage = PER_PAGE) {
      const res = await api.readTextImagePage({
        sort: { field: "dtUpdate", order: "desc" },
        filter: { status: 1 },
        pagination: { perPage },
        cursor,
      });
      this.updateTextImageMap(res.page.data);
      return res.page.data;
    },
    async fetchDislikedTextImage(cursor?: string, perPage = PER_PAGE) {
      const res = await api.readTextImagePage({
        sort: { field: "dtUpdate", order: "desc" },
        filter: { status: -1 },
        pagination: { perPage },
        cursor,
      });
      this.updateTextImageMap(res.page.data);
      return res.page.data;
    },
    async like(targetId: number, toGallery = false) {
      const targetItem = this.map[targetId];
      if (targetItem.status !== 1) {
        const oldLength = this.unmarkedIdList.data.length;
        this.unmarkedIdList.data = this.unmarkedIdList.data.filter(
          (id) => id !== targetId
        );
        if (oldLength !== this.unmarkedIdList.data.length) {
          this.unmarkedCount = Math.max(0, this.unmarkedCount - 1);
        }
        this.dislikeIdList.data = this.dislikeIdList.data.filter(
          (id) => id !== targetId
        );
      }
      const params: Parameters<typeof api.updateOneTextImage>[1] = {
        status: 1,
      };
      if (toGallery) {
        params.dtPublish = new Date().toISOString();
      }
      await api.updateOneTextImage(targetId, params);
      await this.getNewerLikeList();
    },
    async dislike(targetId: number) {
      // const targetItem = this.map[targetId];
      const oldLength = this.unmarkedIdList.data.length;
      this.unmarkedIdList.data = this.unmarkedIdList.data.filter(
        (id) => id !== targetId
      );
      if (oldLength !== this.unmarkedIdList.data.length) {
        this.unmarkedCount = Math.max(0, this.unmarkedCount - 1);
      }
      this.likedIdList.data = this.likedIdList.data.filter(
        (id) => id !== targetId
      );
      await api.updateOneTextImage(targetId, { status: -1 });
      await this.getNewerDislikeList();
    },
    async createTask(params: {
      prompt: string;
      amount: number;
      sourceId?: number;
      style: number;
      mateTraitId?: number;
    }) {
      await api.createOneTextImageTask(params);
      await this.initQueuedListIfAbsent(true);
    },
    async createGifTask(
      params: Parameters<typeof api.createOneTextGifTask>[0]
    ) {
      await api.createOneTextGifTask(params);
      await this.initQueuedListIfAbsent(true);
    },
    async disableCache() {
      this.unmarkedIdList.data = [];
      this.unmarkedIdList.init = false;
      this.queuedIdList.data = [];
      this.queuedIdList.init = false;
      this.likedIdList.data = [];
      this.likedIdList.init = false;
      this.dislikeIdList.data = [];
      this.dislikeIdList.init = false;
      await this.fetchUnmarkedCount();
    },
    updateTextImageMap(data: TextImage[]) {
      data.forEach((item) => {
        this.map[item.id] = item;
      });
    },
    async getOrFetchTextImageById(id: number) {
      if (!this.map[id]) {
        const res = await api.readTextImagePage({ filter: { id } });
        this.updateTextImageMap(res.page.data);
      }
      return this.map[id];
    },
    async getOrFetchTextImageByUUID(uuid: string) {
      const target = Object.values(this.map).find(
        (item) => item.taskId === uuid
      );
      if (!target) {
        const res = await api.readTextImageByUuid(uuid);
        this.updateTextImageMap([res.item]);
        return this.map[res.item.id];
      } else {
        return target;
      }
    },
    async onTaskFinished(id: number) {
      this.queuedIdList.data = this.queuedIdList.data.filter(
        (item) => item !== id
      );
      await this.getNewerUnmarkedList();
    },
    async fetchUnmarkedCount() {
      const res = await api.readUnmarkedTaskCount();
      this.unmarkedCount = res.data.waitingMarkCount;
    },
    async createStoryVideo(
      params: Parameters<typeof api.createOneStoryVideo>[0]
    ) {
      const res = await api.createOneStoryVideo(params);
      await this.initQueuedListIfAbsent(true);
      return res.item;
    },
  },
});
